import React, { useState, useEffect } from 'react';
import { Box, Grid, Paper, Typography, List, ListItem, ListItemText, TextField, Button, Card, CardContent, CardMedia, Input, ListItemIcon, FormControl, Select, MenuItem, InputAdornment, InputLabel, IconButton, Tooltip, Collapse, Chip, Breadcrumbs, Link } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import LocationSelector from '../components/LocationSelector';
import { 
  LocalFlorist,
  Nature,
  Spa,
  Healing,
  Park,
  Search,
  Store,
  LocationOn,
  Close,
  ExpandLess,
  ExpandMore,
  FilterList,
  Sort,
  GridView,
  ViewList,
  NavigateNext,
  Home
} from '@mui/icons-material';

// Add keyframes for cursor blinking
const keyframes = `
  @keyframes blink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0; }
  }
`;

// Inject the keyframes into the document
const injectGlobalStyles = () => {
  const styleEl = document.createElement('style');
  styleEl.innerHTML = keyframes;
  document.head.appendChild(styleEl);
  return () => document.head.removeChild(styleEl);
};

// Styled components
const Sidebar = styled(Paper)(({ theme }) => ({
  position: 'sticky',
  top: theme.spacing(2),
  maxWidth: '260px',
  width: '100%',
}));

const CategoryList = styled(List)(({ theme }) => ({
  maxHeight: '50vh',
  overflow: 'auto',
  scrollbarWidth: 'none', // Firefox
  msOverflowStyle: 'none', // IE and Edge
  '&::-webkit-scrollbar': {
    display: 'none', // Chrome, Safari and Opera
  },
}));

const StoreList = styled(List)(({ theme }) => ({
  maxHeight: '50vh',
  overflow: 'auto',
  scrollbarWidth: 'none', // Firefox
  msOverflowStyle: 'none', // IE and Edge
  '&::-webkit-scrollbar': {
    display: 'none', // Chrome, Safari and Opera
  },
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(45deg, #5e35b1 30%, #1e88e5 90%)',
  color: 'white',
  padding: theme.spacing(1.5),
  display: 'flex',
  alignItems: 'center',
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(2),
}));

const MainContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const PlantCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s',
  width: '100%',
  margin: '0 auto',
  '&:hover': {
    transform: 'scale(1.02)',
    boxShadow: theme.shadows[6],
  },
}));

const StyledBadge = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: 10,
  right: 10,
  backgroundColor: theme.palette.primary.main,
  color: 'white',
  padding: '4px 8px',
  borderRadius: 12,
  fontSize: '0.75rem',
  fontWeight: 'bold',
}));

const SearchResultHeader = styled(Paper)(({ theme }) => ({
  position: 'relative',
  minHeight: '60px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[4],
  marginBottom: theme.spacing(3),
  padding: theme.spacing(3),
  overflow: 'hidden',
  zIndex: 1,
}));

const VideoBackground = styled('video')({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  zIndex: -1,
});

const SearchOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  background: 'linear-gradient(45deg, rgba(94, 53, 177, 0.82) 30%, rgba(30, 136, 229, 0.72) 90%)',
  zIndex: -1,
}));

const FilterChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  backgroundColor: theme.palette.primary.main,
  color: 'white',
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  },
}));

// Default logo component for nursery stores
const DefaultStoreLogo = styled(Box)(({ theme }) => ({
  width: 20,
  height: 20,
  backgroundColor: '#e3f2fd',
  borderRadius: 4,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid #bbdefb',
}));

const bazars = [
  { bn: 'সকল বাজার', en: 'All Bazars' },
  { bn: 'চাচকৈড় বাজার', en: 'Chachkoir Bazar' },
  { bn: 'খুবজিপুর বাজার', en: 'Khubipur Bazar' },
  { bn: 'নাজিরপুর বাজার', en: 'Nazirpur Bazar' },
  { bn: 'বিলদোহর বাজার', en: 'Bildohar Bazar' },
  { bn: 'চাটখিল বাজার', en: 'Chatkhil Bazar' },
  { bn: 'সোনাইমুড়ি বাজার', en: 'Sonaimuri Bazar' },
  { bn: 'কেন্দ্রীয় বাজার', en: 'Central Bazar' },
  { bn: 'নিউ মার্কেট', en: 'New Market' },
  { bn: 'গুলিস্তান বাজার', en: 'Gulistan Bazar' },
  { bn: 'মৌচাক বাজার', en: 'Mouchak Bazar' },
  { bn: 'রকেট বাজার', en: 'Rocket Bazar' },
  { bn: 'কাওরান বাজার', en: 'Kawran Bazar' },
  { bn: 'ফার্মগেট বাজার', en: 'Farmgate Bazar' },
  { bn: 'মিরপুর বাজার', en: 'Mirpur Bazar' },
  { bn: 'উত্তরা বাজার', en: 'Uttara Bazar' },
  { bn: 'ধানমন্ডি বাজার', en: 'Dhanmondi Bazar' },
  { bn: 'মোহাম্মদপুর বাজার', en: 'Mohammadpur Bazar' },
  { bn: 'বনানী বাজার', en: 'Banani Bazar' },
  { bn: 'গুলশান বাজার', en: 'Gulshan Bazar' }
];

// Nursery categories
const nurseryItems = [
  {
    category: { bangla: "ফুল গাছ", english: "Flowering Plants" },
    icon: <LocalFlorist />,
    items: [
      { bangla: "গোলাপ", english: "Rose", icon: <LocalFlorist /> },
      { bangla: "গাঁদা", english: "Marigold", icon: <LocalFlorist /> },
      { bangla: "চামেলি", english: "Jasmine", icon: <LocalFlorist /> },
      { bangla: "বেলী", english: "Beli", icon: <LocalFlorist /> },
      { bangla: "হিবিসকাস", english: "Hibiscus", icon: <LocalFlorist /> }
    ]
  },
  {
    category: { bangla: "ফল গাছ", english: "Fruit Trees" },
    icon: <Nature />,
    items: [
      { bangla: "আম", english: "Mango", icon: <Nature /> },
      { bangla: "কাঁঠাল", english: "Jackfruit", icon: <Nature /> },
      { bangla: "লিচু", english: "Litchi", icon: <Nature /> },
      { bangla: "আমরুদ", english: "Guava", icon: <Nature /> },
      { bangla: "পেঁপে", english: "Papaya", icon: <Nature /> }
    ]
  },
  {
    category: { bangla: "সবজি গাছ", english: "Vegetable Plants" },
    icon: <Spa />,
    items: [
      { bangla: "লাউ", english: "Bottle Gourd", icon: <Spa /> },
      { bangla: "পুঁই শাক", english: "Malabar Spinach", icon: <Spa /> },
      { bangla: "টমেটো", english: "Tomato", icon: <Spa /> },
      { bangla: "বেগুন", english: "Brinjal", icon: <Spa /> },
      { bangla: "ঢেঁড়শ", english: "Okra", icon: <Spa /> }
    ]
  },
  {
    category: { bangla: "ওষধি গাছ", english: "Medicinal Plants" },
    icon: <Healing />,
    items: [
      { bangla: "তুলসী", english: "Basil", icon: <Healing /> },
      { bangla: "আলোভেরা", english: "Aloe Vera", icon: <Healing /> },
      { bangla: "নিম", english: "Neem", icon: <Healing /> },
      { bangla: "হলুদ গাছ", english: "Turmeric Plant", icon: <Healing /> },
      { bangla: "অশ্বগন্ধা", english: "Ashwagandha", icon: <Healing /> }
    ]
  },
  {
    category: { bangla: "শোভাময় গাছ", english: "Ornamental Plants" },
    icon: <Park />,
    items: [
      { bangla: "মানিপ্লান্ট", english: "Money Plant", icon: <Park /> },
      { bangla: "ক্রোটন", english: "Croton", icon: <Park /> },
      { bangla: "অর্কিড", english: "Orchid", icon: <Park /> },
      { bangla: "স্নেক প্ল্যান্ট", english: "Snake Plant", icon: <Park /> },
      { bangla: "বনসাই", english: "Bonsai", icon: <Park /> }
    ]
  }
];

// List of nursery stores
const stores = [
  {
    name: 'গ্রিন গার্ডেন',
    logo: '/images/logos/green-garden.png',
    description: 'সেরা মানের গাছ ও বাগান সামগ্রী'
  },
  {
    name: 'ফ্লাওয়ার ভ্যালি',
    logo: '/images/logos/flower-valley.png',
    description: 'ফুলের গাছের বিশেষজ্ঞ'
  },
  {
    name: 'ইকো নার্সারি',
    logo: '/images/logos/eco-nursery.png',
    description: 'পরিবেশবান্ধব গাছপালা'
  },
  {
    name: 'হার্বাল গার্ডেন',
    logo: '/images/logos/herbal-garden.png',
    description: 'ওষধি গাছের বিশেষজ্ঞ'
  },
  {
    name: 'রয়েল নার্সারি',
    logo: '/images/logos/royal-nursery.png',
    description: 'উন্নত মানের গাছ ও সেবা'
  },
  {
    name: 'বনসাই হাউস',
    logo: '/images/logos/bonsai-house.png',
    description: 'বনসাই ও শোভাময় গাছের বিশেষজ্ঞ'
  }
];

// Sample plants search results data
const searchResultsData = [
  {
    id: 1,
    name: 'গোলাপ গাছ',
    englishName: 'Rose Plant',
    category: 'গোলাপ',
    store: 'গ্রিন গার্ডেন',
    price: '৳১৫০.০০',
    image: '/images/nursery/golap.avif',
    description: 'সুগন্ধি লাল গোলাপের গাছ',
    variety: 'লাল গোলাপ',
    rating: 4.5,
    reviews: 25
  },
  {
    id: 2,
    name: 'আম গাছ',
    englishName: 'Mango Tree',
    category: 'আম',
    store: 'ফ্লাওয়ার ভ্যালি',
    price: '৳৮৫০.০০',
    image: '/images/nursery/amgach.jpg',
    description: 'হিমসাগর জাতের আম গাছ',
    variety: 'হিমসাগর',
    rating: 4.8,
    reviews: 42
  },
  {
    id: 3,
    name: 'তুলসী গাছ',
    englishName: 'Basil Plant',
    category: 'তুলসী',
    store: 'হার্বাল গার্ডেন',
    price: '৳৮০.০০',
    image: '/images/nursery/tulshi.jpeg',
    description: 'পবিত্র তুলসী গাছ',
    variety: 'শ্বেত তুলসী',
    rating: 4.3,
    reviews: 18
  },
  {
    id: 4,
    name: 'টমেটো চারা',
    englishName: 'Tomato Seedling',
    category: 'টমেটো',
    store: 'ইকো নার্সারি',
    price: '৳২৫.০০',
    image: '/images/nursery/tomato.jpg',
    description: 'উন্নত জাতের টমেটো চারা',
    variety: 'হাইব্রিড',
    rating: 4.1,
    reviews: 67
  },
  {
    id: 5,
    name: 'মানিপ্লান্ট',
    englishName: 'Money Plant',
    category: 'মানিপ্লান্ট',
    store: 'রয়েল নার্সারি',
    price: '৳১২০.০০',
    image: '/images/nursery/moneyplant.jpg',
    description: 'ঘর সাজানোর জন্য আদর্শ',
    variety: 'গোল্ডেন',
    rating: 4.6,
    reviews: 31
  },
  {
    id: 6,
    name: 'বনসাই গাছ',
    englishName: 'Bonsai Tree',
    category: 'বনসাই',
    store: 'বনসাই হাউস',
    price: '৳১৫০০.০০',
    image: '/images/nursery/bonsai.jpg',
    description: 'সুন্দর বনসাই গাছ',
    variety: 'ফিকাস',
    rating: 4.9,
    reviews: 12
  },
  {
    id: 7,
    name: 'গাঁদা ফুল',
    englishName: 'Marigold',
    category: 'গাঁদা',
    store: 'গ্রিন গার্ডেন',
    price: '৳৪০.০০',
    image: '/images/nursery/gada.jpg',
    description: 'রঙিন গাঁদা ফুলের গাছ',
    variety: 'কমলা গাঁদা',
    rating: 4.2,
    reviews: 89
  },
  {
    id: 8,
    name: 'চামেলি গাছ',
    englishName: 'Jasmine Plant',
    category: 'চামেলি',
    store: 'ফ্লাওয়ার ভ্যালি',
    price: '৳১৮০.০০',
    image: '/images/nursery/chameli.jpg',
    description: 'সুগন্ধি চামেলি ফুলের গাছ',
    variety: 'সাদা চামেলি',
    rating: 4.7,
    reviews: 34
  }
];

const SearchResult = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const initialQuery = searchParams.get('q') || '';
  
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubCategory, setSelectedSubCategory] = useState('');
  const [selectedStore, setSelectedStore] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState('সকল জেলা');
  const [selectedUpazilla, setSelectedUpazilla] = useState('সকল উপজেলা');
  const [selectedBazar, setSelectedBazar] = useState('সকল বাজার');
  const [sortBy, setSortBy] = useState('relevance');
  const [viewMode, setViewMode] = useState('grid');
  const [expandedCategories, setExpandedCategories] = useState({});
  const [showFilters, setShowFilters] = useState(false);

  // Inject keyframes for the blinking cursor animation
  useEffect(() => {
    const cleanup = injectGlobalStyles();
    return cleanup;
  }, []);

  const handleCategoryClick = (categoryKey) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryKey]: !prev[categoryKey]
    }));
  };

  const handleSubCategoryClick = (mainCategoryKey, subCategory) => {
    setSelectedCategory(mainCategoryKey);
    setSelectedSubCategory(subCategory);
  };

  const handleStoreClick = (store) => {
    setSelectedStore(store.name);
  };

  const handleDistrictChange = (event) => {
    setSelectedDistrict(event.target.value);
  };

  const handleUpazillaChange = (event) => {
    setSelectedUpazilla(event.target.value);
  };

  const handleBazarChange = (event) => {
    setSelectedBazar(event.target.value);
  };

  const handleSortChange = (event) => {
    setSortBy(event.target.value);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    // Update URL with new search query
    navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
  };

  const clearFilters = () => {
    setSelectedCategory('');
    setSelectedSubCategory('');
    setSelectedStore('');
    setSearchQuery('');
    navigate('/search');
  };

  const removeFilter = (filterType) => {
    switch (filterType) {
      case 'category':
        setSelectedCategory('');
        setSelectedSubCategory('');
        break;
      case 'store':
        setSelectedStore('');
        break;
      case 'search':
        setSearchQuery('');
        navigate('/search');
        break;
      default:
        break;
    }
  };

  // Filter and sort results
  let filteredResults = searchResultsData.filter((plant) => {
    const matchesCategory = !selectedCategory || 
      (selectedSubCategory ? plant.category === selectedSubCategory : 
       nurseryItems.find(cat => cat.category.bangla === selectedCategory)?.items.some(item => item.bangla === plant.category));
    const matchesStore = !selectedStore || plant.store === selectedStore;
    const matchesSearch = !searchQuery || 
      plant.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      plant.englishName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      plant.variety.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesStore && matchesSearch;
  });

  // Sort results
  if (sortBy === 'price-low') {
    filteredResults.sort((a, b) => parseFloat(a.price.replace('৳', '').replace('.০০', '')) - parseFloat(b.price.replace('৳', '').replace('.০০', '')));
  } else if (sortBy === 'price-high') {
    filteredResults.sort((a, b) => parseFloat(b.price.replace('৳', '').replace('.০০', '')) - parseFloat(a.price.replace('৳', '').replace('.০০', '')));
  } else if (sortBy === 'rating') {
    filteredResults.sort((a, b) => b.rating - a.rating);
  } else if (sortBy === 'popular') {
    filteredResults.sort((a, b) => b.reviews - a.reviews);
  }

  // Active filters
  const activeFilters = [];
  if (searchQuery) activeFilters.push({ type: 'search', label: `খোঁজ: ${searchQuery}` });
  if (selectedSubCategory) activeFilters.push({ type: 'category', label: `বিভাগ: ${selectedSubCategory}` });
  if (selectedStore) activeFilters.push({ type: 'store', label: `দোকান: ${selectedStore}` });

  return (
    <>
      <Header />
      <Box sx={{ flexGrow: 1, p: 2, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
        {/* Breadcrumbs */}
        <Breadcrumbs 
          separator={<NavigateNext fontSize="small" />} 
          sx={{ mb: 2 }}
        >
          <Link 
            underline="hover" 
            color="inherit" 
            onClick={() => navigate('/')}
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          >
            <Home sx={{ mr: 0.5, fontSize: 20 }} />
            হোম
          </Link>
          <Link 
            underline="hover" 
            color="inherit" 
            onClick={() => navigate('/nursery')}
            sx={{ cursor: 'pointer' }}
          >
            নার্সারি বাজার
          </Link>
          <Typography color="text.primary">
            সার্চ রেজাল্ট
          </Typography>
        </Breadcrumbs>

        {/* Search Header */}
        <SearchResultHeader>
          <VideoBackground autoPlay muted loop>
            <source src="/videos/food.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </VideoBackground>
          <SearchOverlay />
          {/* Location Selector */}
          <LocationSelector 
            variant="search-bar"
            onLocationChange={(location) => {
              console.log('Location changed:', location);
            }}
          />

          {/* Search Field */}
          <form onSubmit={handleSearchSubmit} style={{ flex: 1, marginLeft: 16, marginRight: 16 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="গাছের নাম, ক্যাটেগরি বা নার্সারির নাম লিখুন..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.92)',
                borderRadius: 2,
                boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2
                },
                zIndex: 2,
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <Button 
                      type="submit" 
                      variant="contained" 
                      sx={{ 
                        mr: -1,
                        borderRadius: '0 8px 8px 0'
                      }}
                    >
                      খুঁজুন
                    </Button>
                  </InputAdornment>
                )
              }}
            />
          </form>
        </SearchResultHeader>

        {/* Results Summary and Controls */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 2,
          flexWrap: 'wrap',
          gap: 2
        }}>
          <Box>
            <Typography variant="h6" component="div">
              {filteredResults.length} টি ফলাফল পাওয়া গেছে
              {searchQuery && ` "${searchQuery}" এর জন্য`}
            </Typography>
            
            {/* Active Filters */}
            {activeFilters.length > 0 && (
              <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {activeFilters.map((filter, index) => (
                  <FilterChip
                    key={index}
                    label={filter.label}
                    onDelete={() => removeFilter(filter.type)}
                    deleteIcon={<Close />}
                    size="small"
                  />
                ))}
                <Button
                  size="small"
                  onClick={clearFilters}
                  sx={{ ml: 1 }}
                >
                  সব ফিল্টার মুছুন
                </Button>
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Sort Dropdown */}
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>সাজান</InputLabel>
              <Select
                value={sortBy}
                label="সাজান"
                onChange={handleSortChange}
                startAdornment={<Sort sx={{ mr: 1 }} />}
              >
                <MenuItem value="relevance">প্রাসঙ্গিকতা</MenuItem>
                <MenuItem value="price-low">দাম (কম থেকে বেশি)</MenuItem>
                <MenuItem value="price-high">দাম (বেশি থেকে কম)</MenuItem>
                <MenuItem value="rating">রেটিং</MenuItem>
                <MenuItem value="popular">জনপ্রিয়তা</MenuItem>
              </Select>
            </FormControl>

            {/* View Mode Toggle */}
            <Box sx={{ display: 'flex', border: '1px solid #ddd', borderRadius: 1 }}>
              <IconButton
                size="small"
                onClick={() => setViewMode('grid')}
                sx={{ 
                  backgroundColor: viewMode === 'grid' ? 'primary.main' : 'transparent',
                  color: viewMode === 'grid' ? 'white' : 'inherit',
                  borderRadius: '4px 0 0 4px'
                }}
              >
                <GridView />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => setViewMode('list')}
                sx={{ 
                  backgroundColor: viewMode === 'list' ? 'primary.main' : 'transparent',
                  color: viewMode === 'list' ? 'white' : 'inherit',
                  borderRadius: '0 4px 4px 0'
                }}
              >
                <ViewList />
              </IconButton>
            </Box>

            {/* Filter Toggle for Mobile */}
            <Button
              variant="outlined"
              startIcon={<FilterList />}
              onClick={() => setShowFilters(!showFilters)}
              sx={{ display: { xs: 'flex', md: 'none' } }}
            >
              ফিল্টার
            </Button>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
          {/* Left Sidebar - Filters */}
          <Box sx={{ 
            width: { xs: '100%', md: '260px' }, 
            minWidth: { md: '260px' },
            pr: { md: 2 }, 
            mb: { xs: 2, md: 0 },
            display: { xs: showFilters ? 'block' : 'none', md: 'block' }
          }}>
            <Sidebar elevation={3}>
              <Box sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <SectionHeader>
                  <LocalFlorist sx={{ mr: 1 }} />
                  <Typography variant="h6" component="div">
                    গাছের ধরন
                  </Typography>
                </SectionHeader>
                <CategoryList sx={{ pl: 1, pr: 1 }}>
                  {nurseryItems.map((categoryData) => {
                    const categoryKey = categoryData.category.bangla;
                    return (
                      <React.Fragment key={categoryKey}>
                        <ListItem
                          button
                          onClick={() => handleCategoryClick(categoryKey)}
                          selected={selectedCategory === categoryKey}
                          sx={{ py: 1 }}
                        >
                          <ListItemIcon>
                            {categoryData.icon}
                          </ListItemIcon>
                          <ListItemText 
                            primary={categoryData.category.bangla}
                            secondary={categoryData.category.english}
                            primaryTypographyProps={{ 
                              style: { 
                                fontWeight: selectedCategory === categoryKey ? 'bold' : 'normal',
                                fontSize: '0.9rem'
                              } 
                            }}
                            secondaryTypographyProps={{ 
                              style: { 
                                fontSize: '0.75rem',
                                color: 'text.secondary'
                              } 
                            }}
                          />
                          {expandedCategories[categoryKey] ? <ExpandLess /> : <ExpandMore />}
                        </ListItem>
                        <Collapse in={expandedCategories[categoryKey]} timeout="auto" unmountOnExit>
                          <List component="div" disablePadding>
                            {categoryData.items.map((item) => (
                              <ListItem
                                button
                                key={`${categoryKey}-${item.bangla}`}
                                sx={{ pl: 4, py: 0.5 }}
                                selected={selectedCategory === categoryKey && selectedSubCategory === item.bangla}
                                onClick={() => handleSubCategoryClick(categoryKey, item.bangla)}
                              >
                                <ListItemIcon>
                                  {item.icon}
                                </ListItemIcon>
                                <ListItemText 
                                  primary={item.bangla}
                                  secondary={item.english}
                                  primaryTypographyProps={{ 
                                    style: { 
                                      fontSize: '0.8rem',
                                      fontWeight: selectedCategory === categoryKey && selectedSubCategory === item.bangla ? 'bold' : 'normal'
                                    } 
                                  }}
                                  secondaryTypographyProps={{ 
                                    style: { fontSize: '0.7rem' } 
                                  }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Collapse>
                      </React.Fragment>
                    );
                  })}
                </CategoryList>

                <SectionHeader sx={{ mt: 2 }}>
                  <Store sx={{ mr: 1 }} />
                  <Typography variant="h6" component="div">
                    নার্সারির তালিকা
                  </Typography>
                </SectionHeader>
                <StoreList sx={{ pl: 1, pr: 1 }}>
                  {stores.map((store) => (
                    <ListItem
                      button
                      key={store.name}
                      selected={selectedStore === store.name}
                      onClick={() => handleStoreClick(store)}
                      sx={{ py: 1 }}
                    >
                      <ListItemIcon sx={{ minWidth: 35 }}>
                        <DefaultStoreLogo>
                          <LocalFlorist sx={{ fontSize: 12, color: '#5e35b1' }} />
                        </DefaultStoreLogo>
                      </ListItemIcon>
                      <ListItemText 
                        primary={store.name}
                        secondary={store.description}
                        primaryTypographyProps={{ 
                          fontSize: '0.875rem',
                          fontWeight: selectedStore === store.name ? 'bold' : 'normal' 
                        }}
                        secondaryTypographyProps={{ 
                          fontSize: '0.75rem',
                          color: 'text.secondary'
                        }}
                      />
                    </ListItem>
                  ))}
                </StoreList>
              </Box>
            </Sidebar>
          </Box>

          {/* Main Content - Search Results */}
          <Box sx={{ 
            width: { xs: '100%', md: 'calc(100% - 260px)' },
            flexGrow: 1 
          }}>
            <MainContent>
              {/* Results Grid/List */}
              {filteredResults.length > 0 ? (
                <Box
                  sx={{
                    display: 'grid',
                    gap: 3,
                    gridTemplateColumns: viewMode === 'grid' ? {
                      xs: 'repeat(1, 1fr)', // Mobile portrait: 1 card per row
                      sm: 'repeat(2, 1fr)', // Mobile horizontal: 2 cards per row
                      md: 'repeat(3, 1fr)', // Tablet: 3 cards per row
                      lg: 'repeat(4, 1fr)', // Desktop: 4 cards per row
                      xl: 'repeat(5, 1fr)', // Large desktop: 5 cards per row
                    } : 'repeat(1, 1fr)', // List view: 1 card per row
                    width: '100%',
                  }}
                >
                  {filteredResults.map((plant) => (
                    <Box key={plant.id} sx={{ display: 'flex', width: '100%' }}>
                      <PlantCard sx={{ 
                        width: '100%',
                        flexDirection: viewMode === 'list' ? 'row' : 'column',
                        height: viewMode === 'list' ? 'auto' : '100%'
                      }}>
                        <Box sx={{ 
                          position: 'relative',
                          width: viewMode === 'list' ? 200 : '100%',
                          flexShrink: 0
                        }}>
                          <CardMedia
                            component="img"
                            height={viewMode === 'list' ? 150 : 160}
                            image={plant.image}
                            alt={plant.name}
                            sx={{ 
                              objectFit: 'cover', 
                              backgroundColor: '#f5f5f5', 
                              pt: viewMode === 'list' ? 0 : 1
                            }}
                            onError={(e) => {
                              e.target.src = '/images/placeholder.png';
                            }}
                          />
                          <StyledBadge>{plant.category}</StyledBadge>
                        </Box>
                        <CardContent sx={{
                          flexGrow: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          height: '100%',
                          pb: '16px !important',
                          width: viewMode === 'list' ? 'calc(100% - 200px)' : '100%'
                        }}>
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography gutterBottom variant="h6" component="div">
                              {plant.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {plant.englishName}
                            </Typography>
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="body2" color="text.primary">
                                <strong>জাত:</strong> {plant.variety}
                              </Typography>
                              <Typography variant="body2" color="text.primary">
                                <strong>নার্সারি:</strong> {plant.store}
                              </Typography>
                              <Typography variant="body2" color="text.primary">
                                <strong>রেটিং:</strong> ⭐ {plant.rating} ({plant.reviews} রিভিউ)
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{
                            mt: 'auto',
                            pt: 2
                          }}>
                            <Typography variant="h6" color="primary.main" sx={{ mb: 1, textAlign: 'center' }}>
                              {plant.price}
                            </Typography>
                            <Box sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              gap: 1
                            }}>
                              <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                fullWidth
                              >
                                কিনুন
                              </Button>
                              <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                fullWidth
                              >
                                ব্যাগে নিন
                              </Button>
                            </Box>
                          </Box>
                        </CardContent>
                      </PlantCard>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Paper sx={{ p: 4, textAlign: 'center', mt: 2 }}>
                  <Search sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    কোন ফলাফল পাওয়া যায়নি
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {searchQuery ? 
                      `"${searchQuery}" এর জন্য কোন ফলাফল পাওয়া যায়নি। অনুগ্রহ করে অন্য কিছু খুঁজে দেখুন।` :
                      'অনুগ্রহ করে আপনার ফিল্টার বা সার্চ পরিবর্তন করুন'
                    }
                  </Typography>
                  <Button 
                    variant="outlined" 
                    sx={{ mt: 2 }}
                    onClick={clearFilters}
                  >
                    সব ফিল্টার মুছুন
                  </Button>
                </Paper>
              )}
            </MainContent>
          </Box>
        </Box>
      </Box>
      <Box sx={{ mt: "50px" }}>
        <Footer />
      </Box>
    </>
  );
};

export default SearchResult; 